/**
 * ONE THREAD BUDGET FOR BOTH WORKER POOLS.
 *
 * The terrain pool and the mesh pool each sized themselves as `min(6, cores - 2)` — independently.
 * On an 8-thread laptop that is 6 + 6 = 12 workers, plus the main thread, plus Chrome's GPU process
 * and compositor, on 8 hardware threads (4 physical cores). While a pan streams fresh terrain every
 * worker is busy at once, and the main thread and the GPU process get time-sliced against them:
 * the frame stretches even when the main thread's own work is small, which is the shape of the
 * "frame 55 ms, voxel 45 ms, GPU 15 ms" jank on that machine. Web workers have no priority control,
 * so the only lever is how many there are.
 *
 * The budget leaves two hardware threads for the main thread and the GPU process, and splits the
 * rest roughly 2:1 gen:mesh — generation is the slower stage per chunk (noise, stamps, caves) and
 * the mesh pool only ever has to keep up with what generation produces. On 8 threads: 4 + 2. On a
 * 16-thread desktop the old caps still bind (6 + 6). Floors keep a small machine streaming at all.
 */
export function workerThreadBudget(): number {
  const cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 4;
  return Math.max(3, cores - 2);
}

/** Terrain-generation workers: two thirds of the budget, 2–6. */
export function terrainWorkerBudget(): number {
  return Math.max(2, Math.min(6, Math.ceil(workerThreadBudget() * 2 / 3)));
}

/** Mesh workers: what the budget leaves after generation, 1–6. */
export function meshWorkerBudget(): number {
  return Math.max(1, Math.min(6, workerThreadBudget() - terrainWorkerBudget()));
}
